import React from 'react';
import { InfoCircledIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import styles from './BacktestResults.module.css';
import InfoTooltip from '../../components/common/InfoTooltip/InfoTooltip';
import {
  formatRupees,
  formatPct,
  formatProfitFactor,
  pnlColourClass,
  buildPnLTooltip,
  indicatorTooltipText,
} from './backtestHelpers';

/**
 * Backtest results body — summary cards + matches table +
 * skipped trades + performance section.
 *
 * Purely presentational. `results` comes from the parent
 * BacktestPage component after a successful run.
 */
const BacktestResults = ({ results }) => (
  <div className={styles.resultsCard}>
    {/* P&L Overview — founder's headline metrics. Sits
        ABOVE the volume/match stats so the user sees the
        bottom line first. Renders as either:
          - a 4-card grid (Total Net P&L, Win Rate, Total
            Trades, Profit Factor) when sizing is set, or
          - a single banner card explaining what to do when
            sizing is "Not Configured". Never renders fake
            zeros — honesty over compactness. */}
    {results.pnl_summary && !results.pnl_summary.configured && (
      <div className={styles.pnlNotConfigured}>
        <InfoCircledIcon className={styles.pnlNotConfiguredIcon} aria-hidden="true" />
        <div className={styles.pnlNotConfiguredText}>
          <strong>Position sizing not configured</strong>
          <p>
            Set position sizing on this strategy (Edit &rarr; Position
            Sizing) to see Net P&amp;L, Win Rate, and other performance
            metrics here.
          </p>
        </div>
      </div>
    )}

    {/* TEMPORARILY COMMENTED OUT — uncomment when ready to re-enable
    <div className={styles.pnlGrid}>
      <div className={styles.pnlCard}>
        <span className={styles.pnlValue}>
          {results.pnl_summary?.total_trades > 0
            ? formatPct(results.pnl_summary.win_rate_pct)
            : '—'}
        </span>
        <span className={styles.pnlLabel}>
          Win Rate
          <InfoTooltip text="Percentage of completed trades that ended in profit." />
        </span>
      </div>
      <div className={styles.pnlCard}>
        <span className={styles.pnlValue}>
          {results.pnl_summary?.total_trades}
        </span>
        <span className={styles.pnlLabel}>
          Total Trades
          <InfoTooltip text="Number of completed round-trip trades. One trade = one ENTRY + its matching EXIT. An ENTRY still open at the end of the backtest is not counted." />
        </span>
      </div>
      <div className={styles.pnlCard}>
        <span className={styles.pnlValue}>
          {(results.pnl_summary?.winning_trades + results.pnl_summary?.losing_trades) > 0
            ? formatProfitFactor(results.pnl_summary.profit_factor)
            : '—'}
        </span>
        <span className={styles.pnlLabel}>
          Profit Factor
          <InfoTooltip text="Total winnings divided by total losses. Above 1 = profitable strategy. Below 1 = losing strategy. ∞ = wins exist but no losses. — = no winning or losing trades to compare." />
        </span>
      </div>
    </div>
    */}

    <div className={styles.summaryGrid} style={{ gridTemplateColumns: results.pnl_summary?.configured ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)' }}>
      {results.pnl_summary?.configured && (
        <div className={styles.summaryCard}>
          <span
            className={`${styles.summaryValue} ${pnlColourClass(results.pnl_summary.total_net_pl, styles)}`}
          >
            {formatRupees(results.pnl_summary.total_net_pl)}
          </span>
          <span className={styles.summaryLabel}>
            Total Net P&amp;L
            <InfoTooltip text="Net profit or loss across all completed round-trip trades, before brokerage. Formula: sum of (Sell − Buy) × quantity for every closed trade." />
          </span>
        </div>
      )}
      <div className={styles.summaryCard}>
        <span className={styles.summaryValue}>
          {results.summary?.totalCandles ?? 0}
        </span>
        <span className={styles.summaryLabel}>
          Candles Scanned
          <InfoTooltip text="Number of historical candles evaluated in this date range." />
        </span>
      </div>
      <div className={styles.summaryCard}>
        <span className={styles.summaryValue}>
          {results.summary?.totalMatches ?? 0}
        </span>
        <span className={styles.summaryLabel}>
          Signals Found
          <InfoTooltip text="Total entry and exit signals generated in this backtest." />
        </span>
      </div>
      <div className={styles.summaryCard}>
        <span className={styles.summaryValue}>
          {results.summary?.matchRate ?? 0}%
        </span>
        <span className={styles.summaryLabel}>
          Match Rate
          <InfoTooltip text="Percentage of scanned candles that produced a signal (Signals Found ÷ Candles Scanned)." />
        </span>
      </div>
    </div>

    {results.matches?.length > 0 ? (
      // Sticky first column + sticky header — keeps Date/Time
      // and the column titles visible as the user scrolls
      // through a wide indicator set or a long match list.
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.stickyCol}>Date / Time</th>
              {/* Type column removed — its information
                  ("ENTRY / EXIT") is now carried by the
                  Triggered By chip in a semantic form
                  ("Entry met" / "Exit met"). Action column
                  still carries independent info (the actual
                  trade direction executed: BUY / SELL). */}
              <th>Action</th>
              <th>
                Trigger Price
                <InfoTooltip text="Price at the close of the candle when the signal fired. The 'Price' column to the right shows this same value adjusted for brokerage & taxes." />
              </th>
              {/* Four founder-requested columns — only shown
                  when position sizing is configured. Without
                  sizing they would all be '—' (no quantity to
                  multiply against, so no totals, so no point).
                  Order matches the founder's reference table:
                  Quantity → Brokerage → Price (adjusted) →
                  Total Amount → Net P&L. */}
              {results.pnl_summary?.configured && (
                <>
                  <th>
                    Quantity
                    <InfoTooltip text="Number of shares for this trade. Comes from position-sizing config: 'Fixed Quantity' uses the set value directly; 'Fixed Capital' uses floor(capital / entry price)." />
                  </th>
                  <th>
                    Brokerage &amp; Taxes
                    <InfoTooltip text="Brokerage and taxes for this leg only. Computed as gross trade value × the buy- or sell-side percentage you configured. Shows ₹0.00 when brokerage is not configured." />
                  </th>
                  <th>
                    Price
                    <InfoTooltip text="Trigger Price after applying brokerage & taxes. Buy legs get more expensive (+ cost per share); sell legs receive less per share (− cost per share). This is the effective per-share price actually realised." />
                  </th>
                  <th>
                    Total Amount
                    <InfoTooltip text="Price × Quantity for this leg. Equals what actually changed hands: cost paid (buy legs) or proceeds received (sell legs), brokerage already applied." />
                  </th>
                  <th>
                    Net P&amp;L
                    <InfoTooltip text="Realised profit or loss on the round-trip closed by this EXIT row. Formula: Total Sell Amount − Total Buy Amount. ENTRY rows show '—' because P&L is only realised at exit." />
                  </th>
                </>
              )}
              {/* Per-row chip summarises the condition trace
                  from the backend. Sits before the indicator
                  columns so the "why" answer is next to the
                  "what" answer (action / trigger price). */}
              <th>
                Triggered By
                <InfoTooltip text="Click any chip to see exactly which conditions matched at that candle." />
              </th>
              {results.indicatorColumns?.map((col) => (
                <th key={col.key}>
                  {col.label}
                  {col.timeframe ? ` · ${col.timeframe}` : ''}
                  <InfoTooltip text={indicatorTooltipText(col)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.matches.map((m, i) => {
              const hasTrace = Array.isArray(m.condition_results) && m.condition_results.length > 0;
              const totalConds = hasTrace ? m.condition_results.length : 0;
              const matchedConds = hasTrace ? m.condition_results.filter(c => c && c.matched).length : 0;

              return (
                <React.Fragment key={i}>
                  <tr>
                    <td className={styles.stickyCol}>
                      {m.timestamp
                        ? new Date(m.timestamp).toLocaleString('en-IN')
                        : '—'}
                    </td>
                    {/* Type pill cell removed — see header
                        comment for the rationale. */}
                    <td>
                      <span
                        className={`${styles.actionPill} ${
                          m.action === 'BUY'
                            ? styles.buyPill
                            : styles.sellPill
                        }`}
                      >
                        {m.action}
                      </span>
                    </td>
                    {/* Trigger Price — colored by the actual
                        trade direction (m.action), not by
                        signal_type. This matters for short
                        strategies where ENTRY = SELL and
                        EXIT = BUY: the colour follows the
                        trade, not the lifecycle stage. */}
                    <td
                      className={
                        m.trigger_price != null
                          ? (m.action === 'BUY' ? styles.priceBuy : styles.priceSell)
                          : ''
                      }
                    >
                      {m.trigger_price != null
                        ? `₹${Number(m.trigger_price).toFixed(2)}`
                        : '—'}
                    </td>
                    {/* Four founder columns + Net P&L. Same
                        configured-gate as the headers — keep
                        the column count consistent so the
                        table never goes sparse on us. Every
                        field falls back to '—' when the
                        backend couldn't compute it (e.g.,
                        entry row where sizing couldn't size,
                        or exit row where the matching entry
                        had no usable price). */}
                    {results.pnl_summary?.configured && (
                      <>
                        <td>
                          {typeof m.quantity === 'number'
                            ? m.quantity.toLocaleString('en-IN')
                            : '—'}
                        </td>
                        <td>
                          {typeof m.brokerage === 'number'
                            ? formatRupees(m.brokerage)
                            : '—'}
                        </td>
                        {/* Adjusted price — same buy/sell
                            colouring as Trigger Price so the
                            user can scan a column-pair and
                            see "trigger was X, after costs
                            it's Y" at a glance. */}
                        <td
                          className={
                            typeof m.adjusted_price === 'number'
                              ? (m.action === 'BUY' ? styles.priceBuy : styles.priceSell)
                              : ''
                          }
                        >
                          {typeof m.adjusted_price === 'number'
                            ? `₹${m.adjusted_price.toFixed(2)}`
                            : '—'}
                        </td>
                        <td>
                          {typeof m.total_amount === 'number'
                            ? formatRupees(m.total_amount)
                            : '—'}
                        </td>
                        {/* Net P&L cell — ENTRY rows show '—'
                            (no P&L realised yet); EXIT rows
                            show the round-trip net_pl coloured
                            by sign. Tooltip from buildPnLTooltip
                            shows the founder's "Sell − Buy"
                            math directly. */}
                        <td
                          className={
                            m.signal_type === 'EXIT' && typeof m.net_pl === 'number'
                              ? pnlColourClass(m.net_pl, styles)
                              : ''
                          }
                          title={buildPnLTooltip(m)}
                        >
                          {m.signal_type === 'EXIT' && typeof m.net_pl === 'number'
                            ? formatRupees(m.net_pl)
                            : '—'}
                        </td>
                      </>
                    )}
                    {/* Triggered By — static label chip.
                        Just shows "Entry met" or "Exit met"
                        with a colour tint. No click-to-expand
                        (founder decision — the action and
                        signal-type columns already convey the
                        context; the breakdown panel was
                        redundant). For matches missing the
                        trace (older response in-flight) we
                        render a plain '—' with no chip. */}
                    <td>
                      {/* Render branches:
                          1. SL / TSL exit — no condition trace
                             exists for stop-loss exits; show a
                             risk-driven chip in warm-red so it's
                             distinguishable from signal-driven
                             exits. 'SL' = static fixed stop loss
                             (does not trail). 'TSL' = trailing
                             stop (percent or stepped). They
                             share the colour family (both
                             risk-driven) but the label tells
                             them apart.
                          2. TARGET exit — take-profit chip.
                          3. Has trace — entry or exit
                             conditions met, render the
                             matching chip.
                          4. Else — older response without
                             trace data; render '—'. */}
                      {m.triggered_by === 'SL' ? (
                        <span
                          className={`${styles.triggeredByBtn} ${styles.triggeredByTsl}`}
                          title="Fixed stop loss triggered this exit"
                        >
                          Stop Loss
                        </span>
                      ) : m.triggered_by === 'TSL' ? (
                        <span
                          className={`${styles.triggeredByBtn} ${styles.triggeredByTsl}`}
                          title="Trailing stop loss triggered this exit"
                        >
                          TSL Stop
                        </span>
                      ) : m.triggered_by === 'TARGET' ? (
                        <span
                          className={`${styles.triggeredByBtn} ${styles.triggeredByTarget}`}
                          title="Target (take-profit) triggered this exit"
                        >
                          Target
                        </span>
                      ) : hasTrace ? (
                        <span
                          className={`${styles.triggeredByBtn} ${
                            m.signal_type === 'EXIT'
                              ? styles.triggeredByExit
                              : styles.triggeredByEntry
                          }`}
                          title={
                            m.signal_type === 'EXIT'
                              ? `Exit conditions met (${matchedConds} of ${totalConds})`
                              : `Entry conditions met (${matchedConds} of ${totalConds})`
                          }
                        >
                          {m.signal_type === 'EXIT' ? 'Exit met' : 'Entry met'}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    {results.indicatorColumns?.map((col) => (
                      <td key={col.key}>
                        {m.indicator_snapshot?.[col.key] != null
                          ? Number(m.indicator_snapshot[col.key]).toFixed(2)
                          : '—'}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      <div className={styles.emptyState}>
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 40, color: 'var(--text-muted)' }}
        >
          search_off
        </span>
        <p>No signals found in this date range.</p>
        <p className={styles.hint}>
          Try a different date range or adjust your conditions.
        </p>
      </div>
    )}

    {/* Skipped trades — only render when at least one
        ENTRY signal couldn't fire because configured
        capital wasn't enough for one share. This is the
        "real broker would reject the order" case the
        backend explicitly logs. Surfaced honestly so the
        user can see why their backtest has fewer entries
        than the raw condition match count. */}
    {results.skipped_trades?.length > 0 && (
      <div className={styles.skippedCard}>
        <ExclamationTriangleIcon
          className={styles.skippedIcon}
          aria-hidden="true"
        />
        <div className={styles.skippedText}>
          <strong>
            {results.skipped_trades.length}{' '}
            {results.skipped_trades.length === 1 ? 'entry was' : 'entries were'}{' '}
            skipped (insufficient capital)
          </strong>
          <p>
            The configured capital wasn't enough to buy even one share at the
            entry price. A real broker would reject these orders, so the
            backtest skips them too — they're not counted in trade totals.
            Increase Fixed Capital or switch to Fixed Quantity to capture
            these signals.
          </p>
        </div>
      </div>
    )}

    {/* Performance section — founder's full stats list,
        rendered when sizing is configured (even when
        matches is empty, so the user sees 0/0 honestly
        instead of nothing). */}
    {/* TEMPORARILY COMMENTED OUT — uncomment when ready to re-enable
    {results.pnl_summary?.configured && (
      <div className={styles.performanceSection}>
        <div className={styles.performanceHeader}>
          <h3 className={styles.performanceTitle}>Performance</h3>
          <InfoTooltip text="Detailed breakdown of trade outcomes across this backtest." />
        </div>
        <div className={styles.performanceGrid}>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Total Trades</span>
            <span className={styles.perfValue}>
              {results.pnl_summary.total_trades}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Profitable Trades</span>
            <span className={styles.perfValue}>
              {results.pnl_summary.winning_trades}
              {results.pnl_summary.total_trades > 0 && (
                <span className={styles.perfSub}>
                  {' '}({formatPct(results.pnl_summary.win_rate_pct)})
                </span>
              )}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Negative Trades</span>
            <span className={styles.perfValue}>
              {results.pnl_summary.losing_trades}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Breakeven Trades</span>
            <span className={styles.perfValue}>
              {results.pnl_summary.breakeven_trades}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Total Net Profit</span>
            <span className={`${styles.perfValue} ${pnlColourClass(results.pnl_summary.total_net_profit, styles)}`}>
              {formatRupees(results.pnl_summary.total_net_profit)}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Total Net Loss</span>
            <span className={`${styles.perfValue} ${pnlColourClass(results.pnl_summary.total_net_loss, styles)}`}>
              {formatRupees(results.pnl_summary.total_net_loss)}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Total Net P&amp;L</span>
            <span className={`${styles.perfValue} ${pnlColourClass(results.pnl_summary.total_net_pl, styles)}`}>
              {formatRupees(results.pnl_summary.total_net_pl)}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Profit Factor</span>
            <span className={styles.perfValue}>
              {(results.pnl_summary.winning_trades + results.pnl_summary.losing_trades) > 0
                ? formatProfitFactor(results.pnl_summary.profit_factor)
                : '—'}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Avg Profit (winners)</span>
            <span className={`${styles.perfValue} ${pnlColourClass(results.pnl_summary.avg_profit, styles)}`}>
              {formatRupees(results.pnl_summary.avg_profit)}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Avg Loss (losers)</span>
            <span className={`${styles.perfValue} ${pnlColourClass(results.pnl_summary.avg_loss, styles)}`}>
              {formatRupees(results.pnl_summary.avg_loss)}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Largest Win</span>
            <span className={`${styles.perfValue} ${pnlColourClass(results.pnl_summary.largest_win, styles)}`}>
              {formatRupees(results.pnl_summary.largest_win)}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>Largest Loss</span>
            <span className={`${styles.perfValue} ${pnlColourClass(results.pnl_summary.largest_loss, styles)}`}>
              {formatRupees(results.pnl_summary.largest_loss)}
            </span>
          </div>
          <div className={styles.perfItem}>
            <span className={styles.perfLabel}>
              Total Brokerage
              <InfoTooltip text="Sum of brokerage & taxes across all legs of this backtest. Configure buy/sell-side percentages in the strategy form (edit the strategy, Step 2)." />
            </span>
            <span className={styles.perfValue}>
              {formatRupees(results.pnl_summary.total_brokerage, { showSign: false })}
            </span>
          </div>
        </div>
      </div>
    )}
    */}
  </div>
);

export default BacktestResults;
